import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { GlassPanel } from "@/components/GlassPanel";
import { CtaLink } from "@/components/Button";
import { siteConfig } from "@/config/site";
import { routeSeo } from "@/content/seo";

const implemented = [
  "Authorization is policy-governed and deny-by-default: access must be explicitly granted, not implicitly assumed.",
  "Role separation exists between personal and organisation-scoped identities.",
  "Secrets and credentials are never embedded in client-facing frontend code.",
  "Software components carry integrity verification so tampering with a package can be detected.",
  "Execution is currently restricted to trusted, vetted components only. There is no untrusted or arbitrary code execution path.",
];

const inProgress = [
  "Encryption at rest for locally stored secrets is being designed and is not yet complete.",
  "Tenant and organisation data isolation at the storage layer is being scoped before it is wired into live cloud infrastructure.",
  "Automated security scanning and reporting tooling is under active development.",
  "Sandboxed execution of untrusted or third-party components is not yet implemented.",
];

const futureObjectives = [
  "Devory intends to evaluate formal compliance frameworks (such as SOC 2) once the platform and its operating processes are mature enough to support them.",
  "Devory intends to publish a dedicated responsible-disclosure process as the security program matures.",
];

export function SecurityPage() {
  const seo = routeSeo["/security"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/security" />

      <section className="container page-hero">
        <p className="eyebrow">Security</p>
        <h1 className="page-hero-title">Security-by-design, reported without inflation</h1>
        <p className="page-hero-lede">
          Devory's security posture is still developing alongside the product. This page separates
          what is implemented today from what is actively being built and what remains a future
          objective. Devory does not claim SOC 2, ISO 27001, PCI DSS or any other certification
          unless it has genuinely been obtained.
        </p>
      </section>

      <section className="container section" aria-labelledby="implemented-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="implemented-heading">Existing controls</span>}
          />
          <ul className="list-check">
            {implemented.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </GlassPanel>
      </section>

      <section className="container section-tight" aria-labelledby="in-progress-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="in-progress-heading">Controls under development</span>}
          />
          <ul className="list-check">
            {inProgress.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </GlassPanel>
      </section>

      <section className="container section-tight" aria-labelledby="future-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="future-heading">Future compliance objectives</span>}
          />
          <ul className="list-check">
            {futureObjectives.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </GlassPanel>
      </section>

      <section className="container section-tight" aria-labelledby="report-heading">
        <GlassPanel className="cta-banner">
          <h2 id="report-heading">Reporting a vulnerability</h2>
          <p>
            Devory does not yet publish a dedicated, monitored security mailbox. Use the contact
            form and select the <strong>Security</strong> category so your report reaches the right
            people, or email{" "}
            <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a> directly
            with &ldquo;Security&rdquo; in the subject line.
          </p>
          <div className="cta-banner-actions">
            <CtaLink to="/contact" variant="primary">
              Report through the contact form
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
